import { Injectable } from '@angular/core';
import { MarkerClusterer, Cluster } from '@googlemaps/markerclusterer';
import { MapLayerQueryBuildService } from '../services/data/map-layer-query-build.service';
import { ClusterPinModes, Layer, MapWithState } from '../store/cop/models';
import { LayerMapFeatures } from './models/layer-map-features.model';
import { MapDrawingContext } from './models/map-drawing-context.model';
import { MapEventsService } from '../services/map-events.service';
import { LayerClustererFactory, ClusterRenderer } from './clustering';
import { Dataset } from '@app/modules/core/store/models';
import { MapDrawingService } from './map-drawing.service';
import { PopupService } from './popups';

@Injectable({ providedIn: 'root' })
export class MapLayerClusterService {
  private layerClusterers: { [layerUId: string]: MarkerClusterer } = {};
  private clusterRenderers: { [layerUId: string]: ClusterRenderer } = {};
  private clusterMarkers: {
    [id: string]: {
      marker: google.maps.Marker;
      cluster: Cluster;
    };
  } = {};
  private selectedLayerUId: string;
  private selectedMarker: google.maps.Marker;

  private layerMarkersToAdd: {
    [id: string]: {
      groupUId?: string;
      marker: google.maps.Marker;
    }[];
  };
  private layerMarkersToRemove: {
    [id: string]: {
      groupUId?: string;
      marker: google.maps.Marker;
    }[];
  };

  public initCluster(): void {
    if (!!this.layerClusterers) {
      Object.values(this.layerClusterers).forEach(cl => {
        if (!!cl) {
          cl.clearMarkers();
        }
      });
      Object.keys(this.layerClusterers).forEach(l => {
        delete this.layerClusterers[l];
      });
      Object.keys(this.clusterRenderers).forEach(r => {
        delete this.clusterRenderers[r];
      });
    }

    this.layerClusterers = {};
    this.clusterMarkers = {};
    this.clusterRenderers = {};
    this.selectedLayerUId = null;
    this.selectedMarker = null;
  }

  public registerClusterMarker(id: string, cluster: Cluster, marker: google.maps.Marker): void {
    this.clusterMarkers[id] = {
      marker,
      cluster
    };
  }

  public setLayerSettings(
    mapState: MapWithState,
    mapEvents: MapEventsService,
    drawingService: MapDrawingService,
    map: any,
    popup: PopupService
  ) {
    if (!!!mapState?.layerStates || !mapState?.layersSet || !mapState?.datasetsSet || !map) {
      return;
    }

    Object.keys(mapState.layerStates).forEach(layerUId => {
      const layer = mapState?.layersSet[layerUId] || null;
      if (!layer) {
        return;
      }
      const dataset = mapState?.datasetsSet[layer.datasetUId] ?? null;

      if (layer?.isActive === true && Boolean(dataset)) {
        const needsToCluster =
          (MapLayerQueryBuildService.autoClusterNumber > 0 &&
            mapState.layerStates[layerUId].documentCount >= MapLayerQueryBuildService.autoClusterNumber) ||
          layer?.displayOptions?.pinClusteringMode !== ClusterPinModes.Never;

        if (needsToCluster === true) {
          if (!Boolean(this.layerClusterers[layerUId])) {
            this.createLayerClusterer(mapEvents, map, layer, dataset, drawingService.isWorkflowLayerSection(layer), popup);
          }
        } else {
          if (Boolean(this.layerClusterers[layerUId])) {
            this.layerClusterers[layerUId].clearMarkers();
            delete this.layerClusterers[layerUId];
          }
        }
      }
    });
  }

  public hasLayerCluster(layerUId: string): boolean {
    return !!this.layerClusterers[layerUId];
  }

  public deleteLayerCluster(layerUId: string): void {
    const layerHasCluster = this.hasLayerCluster(layerUId);

    if (layerHasCluster) {
      if (!!this.layerClusterers[layerUId]) {
        delete this.layerClusterers[layerUId];
      }
    }
  }

  public clearChanges(layerUId: string, groupUId?: string) {
    if (!!this.layerMarkersToAdd && !!this.layerMarkersToAdd[layerUId]) {
      if (!!!groupUId) {
        this.layerMarkersToAdd[layerUId] = [];
      } else {
        for (let i = this.layerMarkersToAdd[layerUId].length - 1; i >= 0; i--) {
          if (this.layerMarkersToAdd[layerUId][i].groupUId === groupUId) {
            this.layerMarkersToAdd[layerUId].splice(i, 1);
          }
        }
      }
    }
    if (!!this.layerMarkersToRemove && !!this.layerMarkersToRemove[layerUId]) {
      if (!!!groupUId) {
        this.layerMarkersToRemove[layerUId] = [];
      } else {
        for (let i = this.layerMarkersToRemove[layerUId].length - 1; i >= 0; i--) {
          if (this.layerMarkersToRemove[layerUId][i].groupUId === groupUId) {
            this.layerMarkersToRemove[layerUId].splice(i, 1);
          }
        }
      }
    }
  }

  public applyChanges(layerUId: string, groupUId?: string) {
    const clusterer = this.layerClusterers[layerUId];

    if (!!!clusterer) {
      this.clearChanges(layerUId);
      return;
    }

    const hasMarkersToAdd =
      !!this.layerMarkersToAdd &&
      ((!!!groupUId && this.layerMarkersToAdd[layerUId]?.length > 0) ||
        (!!groupUId && this.layerMarkersToAdd[layerUId]?.filter(c => c.groupUId === groupUId).length > 0));

    const hasMarkersToRemove =
      !!this.layerMarkersToRemove &&
      ((!!!groupUId && this.layerMarkersToRemove[layerUId]?.length > 0) ||
        (!!groupUId && this.layerMarkersToRemove[layerUId]?.filter(c => c.groupUId === groupUId).length > 0));

    if (hasMarkersToRemove || hasMarkersToAdd) {
      if (hasMarkersToRemove) {
        if (!!!groupUId) {
          const clusterMarkers = this.layerMarkersToRemove[layerUId].map(c => c.marker);

          clusterer.removeMarkers(clusterMarkers, hasMarkersToAdd);

          clusterMarkers.forEach(marker => {
            google.maps.event.clearInstanceListeners(marker);
            marker.unbindAll();
            marker.setMap(null);
            marker = null;
          });
        } else {
          const clusterMarkers = this.layerMarkersToRemove[layerUId]?.filter(c => c.groupUId === groupUId).map(c => c.marker);

          clusterer.removeMarkers(clusterMarkers, hasMarkersToAdd);

          clusterMarkers.forEach(marker => {
            google.maps.event.clearInstanceListeners(marker);
            marker.unbindAll();
            marker.setMap(null);
            marker = null;
          });
        }
      }
      if (hasMarkersToAdd) {
        if (!!!groupUId) {
          clusterer.addMarkers(
            this.layerMarkersToAdd[layerUId].map(c => c.marker),
            false
          );
        } else {
          clusterer.addMarkers(
            this.layerMarkersToAdd[layerUId]?.filter(c => c.groupUId === groupUId).map(c => c.marker),
            false
          );
        }
      }
    }

    this.clearChanges(layerUId, groupUId);
  }

  public registerMarkerAdd(layerUId: string, marker: google.maps.Marker, groupUId?: string) {
    const isVisible = marker.getVisible()
    if (!isVisible) {
      this.registerMarkerRemove(layerUId, marker, groupUId);
      return;
    }
    if (!!!this.layerMarkersToAdd) {
      this.layerMarkersToAdd = {};
    }

    if (!!!this.layerMarkersToAdd[layerUId]) {
      this.layerMarkersToAdd[layerUId] = [];
    }

    this.layerMarkersToAdd[layerUId].push({ groupUId, marker });
  }

  // TODO: BRENT!!! ADD TYPE
  public registerMarkerRemove(layerUId: string, marker: any, groupUId?: string) {
    if (!!!this.layerMarkersToRemove) {
      this.layerMarkersToRemove = {};
    }

    if (!!!this.layerMarkersToRemove[layerUId]) {
      this.layerMarkersToRemove[layerUId] = [];
    }

    this.layerMarkersToRemove[layerUId].push({ groupUId, marker });
  }

  public removeLayerClusters(drawingContext: MapDrawingContext, layer: Layer) {
    const layerFeatures = drawingContext.mapFeatures[layer.uId];

    if (!!!layerFeatures) {
      return;
    }

    const clusterer = this.layerClusterers[layer.uId];

    if (!!!clusterer) {
      return;
    }

    const markersToRemove = [];

    const docUIds = Object.keys(layerFeatures);
    for (let docUId of docUIds) {
      const docFeatures = layerFeatures[docUId];
      if (!!!docFeatures) {
        continue;
      }

      for (let marker of docFeatures.markers) {
        markersToRemove.push(marker);
      }
    }

    if (markersToRemove.length) {
      clusterer.removeMarkers(markersToRemove, false);

      markersToRemove.forEach(marker => {
        google.maps.event.clearInstanceListeners(marker);
        marker['__abortController']?.abort();
        marker.map = null;
        marker.content = null;
        marker = null;
      });
    }
  }

  public removeLayerClusterDocuments(drawingContext: MapDrawingContext, layer: Layer, documentUIds: string[]) {
    const layerFeatures = drawingContext.mapFeatures[layer.uId];

    if (!!!layerFeatures) {
      return;
    }

    const clusterer = this.layerClusterers[layer.uId];

    if (!!!clusterer) {
      return;
    }

    const markersToRemove = [];

    const docUIds = Object.keys(layerFeatures);
    for (let docUId of docUIds) {
      const docFeatures = layerFeatures[docUId];
      if (!!!docFeatures || !documentUIds.includes(docUId)) {
        continue;
      }

      for (let marker of docFeatures.markers) {
        markersToRemove.push(marker);
      }
    }

    if (markersToRemove.length) {
      clusterer.removeMarkers(markersToRemove, false);

      markersToRemove.forEach(marker => {
        google.maps.event.clearInstanceListeners(marker);
        marker['__abortController']?.abort();
        marker.map = null;
        marker.content = null;
        marker = null;
      });
    }
  }

  public updateLayerClusters(layerFeatures: LayerMapFeatures, layer: Layer, documentUIds: string[]) {
    if (!!!layerFeatures) {
      return;
    }

    const clusterer = this.layerClusterers[layer.uId];

    if (!!!clusterer) {
      return;
    }

    const markersToRemove = [];
    const markersToAdd = [];

    for (let docUId of documentUIds) {
      const docFeatures = layerFeatures[docUId];
      if (!!!docFeatures || !documentUIds.includes(docUId)) {
        continue;
      }

      for (let marker of docFeatures.markers) {
        const isVisible = marker.getVisible();

        if (isVisible) {
          markersToAdd.push(marker);
        } else {
          markersToRemove.push(marker);
        }
      }
    }

    if (markersToRemove.length || markersToAdd.length) {
      if (markersToRemove.length) {
        clusterer.removeMarkers(markersToRemove, markersToAdd.length > 0);

        markersToRemove.forEach(marker => {
          google.maps.event.clearInstanceListeners(marker);
          marker['__abortController']?.abort();
          marker.map = null;
          marker.content = null;
          marker = null;
        });
      }
      if (markersToAdd.length) {
        clusterer.addMarkers(markersToAdd, false);
      }
    }
  }

  private createLayerClusterer(
    mapEvents: MapEventsService,
    map: any,
    layer: Layer,
    dataset: Dataset,
    isWorkflowLayerSection: boolean,
    popup: PopupService
  ): void {
    const { clusterRenderer, markerClusterer } = LayerClustererFactory.generateLayerClusterer(
      this,
      mapEvents,
      map,
      layer,
      dataset,
      isWorkflowLayerSection,
      popup
    );
    this.layerClusterers[layer.uId] = markerClusterer;
    this.clusterRenderers[layer.uId] = clusterRenderer;
  }

  public markClusterSelected(layerUId: string, id: string): void {
    if (!!this.selectedMarker) {
      this.selectedMarker['__data'] = { ...this.selectedMarker['__data'], isSelected: false };
      this.clusterRenderers[this.selectedLayerUId].renderNormal(this.selectedMarker);
    }
    if (!!!this.clusterMarkers[id]) {
      return;
    }
    this.selectedLayerUId = layerUId;
    this.selectedMarker = this.clusterMarkers[id].marker;
    this.clusterMarkers[id].marker['__data'] = { ...this.clusterMarkers[id].marker['__data'], isSelected: true };
    this.clusterRenderers[layerUId].renderSelected(this.clusterMarkers[id].marker);
  }

  public clearClusterSelection(): void {
    if (!!this.selectedMarker) {
      this.selectedMarker['__data'] = { ...this.selectedMarker['__data'], isSelected: false };
      this.clusterRenderers[this.selectedLayerUId].renderNormal(this.selectedMarker);
    }
    this.selectedLayerUId = null;
    this.selectedMarker = null;
  }

  // programmatically set clickable state since TerraDraw does not do it
  public setClickable(clickable: boolean): void {
    Object.values(this.clusterRenderers).forEach(cr => (cr.clickable = clickable));
    Object.values(this.clusterMarkers).forEach(({ marker }) => (marker.setClickable(clickable)));
  }
}
